import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '@/context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function usePaymentStatus(sessionId, clearCart) {
  const [status, setStatus] = useState('checking');
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    let active = true;
    let timeoutId;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === 'paid') {
          setStatus('paid');
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
      } catch { /* A later polling attempt can still confirm payment. */ }
      attempts += 1;
      if (attempts < 10 && active) timeoutId = window.setTimeout(poll, 2000);
      else setStatus('pending');
    };
    poll();
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [clearCart, sessionId]);

  return status;
}

function CheckingState() {
  return <>
    <Loader2 className="animate-spin text-[#657260]" size={32} />
    <p className="mt-6 font-mono2 text-xs uppercase tracking-[0.3em] text-[#5C5C5C]" data-testid="payment-status-text">
      Confirming your payment…
    </p>
  </>;
}

function PaidState({ sessionId }) {
  return <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
    <div className="mx-auto flex h-20 w-20 items-center justify-center bg-[#C7D1C3]"><Check size={34} strokeWidth={2.5} className="text-black" /></div>
    <h1 className="mt-8 font-syne text-4xl font-extrabold uppercase tracking-tight md:text-6xl" data-testid="order-confirmed-title">Order <span className="sage-text">confirmed</span></h1>
    <p className="mx-auto mt-5 max-w-md text-sm font-light leading-relaxed text-[#5C5C5C]">Your pieces are being packed. A confirmation has been sent to your inbox.</p>
    <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#777]" data-testid="order-session-id">Ref — {sessionId?.slice(-12)}</p>
    <Link to="/shop" data-testid="success-continue-shopping" className="mt-10 inline-block bg-[#1E1E1E] px-10 py-4 font-syne text-xs font-bold uppercase tracking-[0.16em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#657260]">Continue shopping</Link>
  </motion.div>;
}

function UnconfirmedState({ status }) {
  const pending = status === 'pending';
  return <>
    <h1 className="font-syne text-3xl font-extrabold uppercase tracking-tight" data-testid="payment-pending-title">Payment {pending ? 'processing' : 'unverified'}</h1>
    <p className="mt-4 max-w-md text-sm text-[#5C5C5C]">{pending ? 'Your payment is taking longer than usual to confirm. It will resolve shortly.' : 'We could not verify this session.'}</p>
    <Link to="/shop" data-testid="pending-back-to-shop" className="mt-8 inline-block border border-[#1E1E1E] px-8 py-3.5 font-syne text-xs font-bold uppercase tracking-[0.16em] text-[#1E1E1E]">Back to shop</Link>
  </>;
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clear } = useCart();
  const status = usePaymentStatus(sessionId, clear);

  return (
    <div className="aura-page-surface flex min-h-screen flex-col items-center justify-center px-6 text-center text-[#1E1E1E]" data-testid="checkout-success-page">
      {status === 'checking' && <CheckingState />}
      {status === 'paid' && <PaidState sessionId={sessionId} />}
      {(status === 'pending' || status === 'error') && <UnconfirmedState status={status} />}
    </div>
  );
}
